import { Link, useSearchParams } from 'react-router'

import { TemplateDownloadList } from '#app/components/mocra/template-download-list.tsx'
import { TEMPLATE_DOWNLOADS } from '#app/components/mocra/template-downloads.ts'
import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { Typography } from '#app/components/ui/typography.tsx'
import { type Route } from '#app/routes/mocra+/_form+/+types/get-started.ts'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'

export const meta: Route.MetaFunction = () => [{ title: 'MoCRA — Get Started' }]

export default function GetStarted() {
	const [searchParams] = useSearchParams()
	const nextStepId = getNextFormStepId('get-started') ?? 'company'
	const nextHref = buildStepPathFromId(nextStepId, searchParams)

	return (
		<Page
			title="Get Started"
			description={
				<div className="leading-6">
					Create your <strong className="text-foreground">FREE</strong> MoCRA
					(Modernization of Cosmetics Regulation Act) SOP (standard operating
					procedure) compliance packet with our software.
				</div>
			}
			className="space-y-6 text-sm leading-6"
		>
			<Divider />
			<section className="space-y-3">
				<Typography.Subtitle>If MoCRA is new to you:</Typography.Subtitle>
				<div className="text-muted-foreground space-y-3">
					<p>
						MoCRA (Modernization of Cosmetics Regulation Act) now requires
						cosmetics companies to:
					</p>
					<ul className="list-inside list-disc space-y-1 pl-2">
						<li>Register manufacturing facilities</li>
						<li>List every product SKU</li>
						<li>Report serious adverse events within 15 days</li>
						<li>Document safety substantiation</li>
					</ul>
					<p>
						The FDA (Food and Drug Administration) can demand records on the
						spot and <strong>suspend your operations</strong> if you cannot
						produce them. Failing to implement these workflows can trigger
						import holds, product seizures, civil money penalties, forced
						recalls, or the loss of key retail partnerships.
					</p>
				</div>
				<p className="text-muted-foreground">
					The questions this intake covers are the ones auditors and retailers
					ask when they assess MoCRA readiness.
				</p>
			</section>
			<Divider />
			<section className="space-y-3">
				<Typography.Subtitle>
					If you're trying to become MoCRA compliant:
				</Typography.Subtitle>
				{/*<ul className="text-muted-foreground list-inside list-disc space-y-1 pl-2">
					<li>Document serious adverse event reporting and escalation paths</li>
					<li>
						Prove facility registration and FDA Establishment Identifier (FEI)
						preparedness
					</li>
					<li>
						Show supplier oversight, recall playbooks, and record-access
						controls
					</li>
					<li>
						Link every policy, SOP, and training record you’ll need during an
						inspection
					</li>
				</ul>*/}
				<p className="text-muted-foreground">
					The SOP package we generate becomes evidence during FDA inspections,
					routine partner audits, and retailer onboarding. Completing it now
					prevents emergency scrambles when the agency or a major customer asks
					about your compliance procedures.
				</p>
			</section>
			<Divider />
			<section className="space-y-3">
				<Typography.Subtitle>Estimated time to complete</Typography.Subtitle>
				<ul className="list-inside list-disc space-y-1 pl-2">
					<li>
						<strong>20 minutes</strong> if you have facility rosters and
						supplier details on hand.
						<ul className="list-inside list-disc space-y-1 pl-5">
							<li>
								<strong>5 minutes</strong> to capture the company profile and
								primary contacts.
							</li>
							<li>
								<strong>10 minutes</strong> to outline products, facilities, and
								suppliers.
							</li>
							<li>
								<strong>5 minutes</strong> for record access, recall readiness,
								and policy references.
							</li>
						</ul>
					</li>
				</ul>
				<p className="text-muted-foreground">
					Progress saves automatically in your browser. You can leave and come
					back without losing work. When all sections are complete, head to
					"Generate package" to produce draft SOPs for review.
				</p>
			</section>
			<Divider />
			<section className="space-y-3">
				<Typography.Subtitle>Download starter templates</Typography.Subtitle>
				<p className="text-muted-foreground">
					Need a fast preview or want to circulate drafts offline? Grab the raw
					markdown SOP templates here and personalize them whenever you are
					ready.
				</p>
				<TemplateDownloadList templates={TEMPLATE_DOWNLOADS} />
			</section>
			<div className="flex justify-end">
				<Button asChild>
					<Link to={nextHref}>Continue</Link>
				</Button>
			</div>
		</Page>
	)
}
